// The unified "Upload" dialog hands what was picked or dropped to the import
// review (helpers/local-folder-import), which reads each file's
// webkitRelativePath: "<root>/<album folder>/.../file". This shapes any mix of
// files and folders into that layout.

// A picked file and where it sits in the selection: "photo.jpg" for a loose
// file, "Trip/day 1/photo.jpg" for one inside a chosen folder.
export type PickedItem = { file: File; path: string };

// Name of the made-up root that holds a mixed selection; the review shows it
// as the upload's title.
export const UPLOAD_ROOT_NAME = 'Your upload';

const withPath = (file: File, path: string): File => {
  // The browser's own webkitRelativePath is a read-only getter on File's
  // prototype; an own property on this object takes its place.
  Object.defineProperty(file, 'webkitRelativePath', {
    value: path,
    configurable: true,
  });
  return file;
};

const isHidden = (path: string) =>
  path.split('/').some((part) => part.startsWith('.'));

export type FolderSummary = { name: string; fileCount: number };

/**
 * What was picked, as the upload flow needs to see it.
 * - pickedFolder: the one folder that was picked (and nothing beside it).
 * - sourceFolder: that folder, when it has subfolders.
 * - groups: the folders that could each become an album: that folder's
 *   subfolders, or else the picked folders themselves.
 * - nested: some group has folders of its own (they can't be kept: albums
 *   don't nest).
 */
export type SelectionShape = {
  totalFiles: number;
  looseFiles: number;
  pickedFolder: FolderSummary | null;
  sourceFolder: FolderSummary | null;
  groups: FolderSummary[];
  nested: boolean;
};

export const describeSelection = (items: PickedItem[]): SelectionShape => {
  const visible = items.filter(({ path }) => !isHidden(path));
  const tops = new Map<string, number>();
  let rootFiles = 0;
  visible.forEach(({ path }) => {
    const parts = path.split('/');
    if (parts.length === 1) rootFiles += 1;
    else tops.set(parts[0], (tops.get(parts[0]) || 0) + 1);
  });

  const onlyFolder = tops.size === 1 && rootFiles === 0;
  const single =
    onlyFolder && visible.some(({ path }) => path.split('/').length > 2);
  // Albums come from one level below the single picked folder; else from the
  // picked folders.
  const depth = single ? 1 : 0;
  const groups = new Map<string, number>();
  let looseFiles = 0;
  let nested = false;
  visible.forEach(({ path }) => {
    const parts = path.split('/');
    if (parts.length <= depth + 1) {
      looseFiles += 1;
      return;
    }
    groups.set(parts[depth], (groups.get(parts[depth]) || 0) + 1);
    if (parts.length > depth + 2) nested = true;
  });

  const [topName] = Array.from(tops.keys());
  const picked = { name: topName, fileCount: visible.length };
  return {
    totalFiles: visible.length,
    looseFiles,
    pickedFolder: onlyFolder ? picked : null,
    sourceFolder: single ? picked : null,
    groups: Array.from(groups, ([name, fileCount]) => ({ name, fileCount })),
    nested,
  };
};

// What to do with the picked folders.
// - albums: each group becomes an album (the default).
// - loose: every file on its own, with no album from the folders (inside an
//   album they go into it; a plain folder picked there works the same way).
// - oneAlbum: the picked folder becomes a single album, subfolders merged.
export type FolderHandling = 'albums' | 'loose' | 'oneAlbum';

/**
 * Files laid out for the import review (see the top of this file):
 * "<root>/<album>/.../file" becomes a memory of that album, "<root>/file" a
 * loose one.
 */
export const toImportFiles = (
  items: PickedItem[],
  handling: FolderHandling = 'albums',
): File[] => {
  const shape = describeSelection(items);
  // .DS_Store and the like are noise, not memories.
  const visible = items.filter(({ path }) => !isHidden(path));
  return visible.map(({ file, path }, index) => {
    if (handling === 'loose') {
      // Numbered: two folders can hold files of the same name, and the
      // review tells files apart by path.
      return withPath(file, `${UPLOAD_ROOT_NAME}/${index}-${file.name}`);
    }
    // One folder with subfolders is already "<root>/<album>/...".
    if (handling === 'albums' && shape.sourceFolder) {
      return withPath(file, path);
    }
    return withPath(file, `${UPLOAD_ROOT_NAME}/${path}`);
  });
};

// Files from a file or folder dialog (a folder dialog fills
// webkitRelativePath; a file dialog leaves it empty).
export const pickedFromInput = (files: File[]): PickedItem[] =>
  files.map((file) => ({ file, path: file.webkitRelativePath || file.name }));

const readFileEntry = (entry: FileSystemFileEntry) =>
  new Promise<File>((resolve, reject) => {
    entry.file(resolve, reject);
  });

// A directory reader hands out its entries in batches until one comes back
// empty.
const readAllEntries = async (reader: FileSystemDirectoryReader) => {
  const all: FileSystemEntry[] = [];
  for (;;) {
    // eslint-disable-next-line no-await-in-loop
    const batch = await new Promise<FileSystemEntry[]>((resolve, reject) => {
      reader.readEntries(resolve, reject);
    });
    if (batch.length === 0) return all;
    all.push(...batch);
  }
};

const walkEntry = async (
  entry: FileSystemEntry,
  prefix: string,
  out: PickedItem[],
): Promise<void> => {
  if (entry.isFile) {
    const file = await readFileEntry(entry as FileSystemFileEntry);
    out.push({ file, path: `${prefix}${file.name}` });
    return;
  }
  if (entry.isDirectory) {
    const children = await readAllEntries(
      (entry as FileSystemDirectoryEntry).createReader(),
    );
    await Promise.all(
      children.map((child) => walkEntry(child, `${prefix}${entry.name}/`, out)),
    );
  }
};

/**
 * Everything dropped, folders included (walked recursively). The entries
 * must be taken from the event before anything is awaited: the browser
 * empties the drop's data afterwards.
 */
export const readDrop = (dataTransfer: DataTransfer): Promise<PickedItem[]> => {
  const entries = Array.from(dataTransfer.items)
    .map((item) => item.webkitGetAsEntry?.())
    .filter((entry): entry is FileSystemEntry => !!entry);
  if (entries.length === 0) {
    return Promise.resolve(
      Array.from(dataTransfer.files).map((file) => ({ file, path: file.name })),
    );
  }
  const out: PickedItem[] = [];
  return Promise.all(entries.map((entry) => walkEntry(entry, '', out))).then(
    () => out,
  );
};
